import { fontBasePixels, viewports } from '@telus-uds/system-constants';

/*
 * Simulates an object that has every positive integer as a property
 * e.g. integer[12345] --> 12345, integer.10px --> undefined
 */
const integer = new Proxy({}, {
  get(self, key) {
    const n = typeof key === 'string' ? Number(key) : key;
    if (!Number.isInteger(n) || n < 0) {
      // React Native does some build-time type sniffing on `.prototype` and `.$$typeof`.
      // Behave like a normal object, return `undefined` or the real default/prototype values.
      return self[key];
    }
    return n;
  }
});

// Add a system size for each viewport
// This adds 'viewportXs', 'viewportSm', 'viewportMd', etc..
const viewportSizes = Object.fromEntries([...viewports.map].map(([viewportName, viewportValue]) => [`viewport${viewportName.charAt(0).toUpperCase()}${viewportName.slice(1)}`, viewportValue]));

// Typescript types; may be used to generate .d.ts
const types = {
  integer: 'Number',
  float: 'Number',
  color: 'String',
  display: 'String',
  string: 'String',
  enum: ({
    values
  }) => `'${Object.entries(values).map(([, value]) => value).join("'|'")}'`,
  size: 'Number|String',
  fontSize: 'Number|String',
  shadow: '{"blur": Number, "color": String, "inset": Boolean, "offsetX": Number, "offsetY": Number, "spread": Number}',
  gradient: '{"type": ["linear"|"radial"], "angle": Number, "stops": Array<{"stop": Number, "color": String }>}'
};
const applyDynamicTypes = tokens => Object.fromEntries(Object.entries(tokens).map(([key, properties]) => {
  const {
    type,
    nullable
  } = properties;
  const typeDef = typeof type === 'function' ? type(properties) : type;
  return [key, {
    ...properties,
    type: nullable ? `${typeDef}|null` : typeDef
  }];
}));

/**
 * Every token type currently used in themes; i.e. every key on `palette` or `system` as
 * used in theme source JSON files. Each token type has these properties:
 *  - `default`: The expected behaviour where a component doesn't apply this token
 *  - `type`: A platform-specific string which can be used in generated type files (e.g. Typescript type)
 *  - `nullable`: If true, `null` (or platform equivalent) is a meaningful, valid value for this token
 *  - `values`: An object of values that can be accessed via `system` like `{system.someType.someValue}`
 */
const tokenTypes = applyDynamicTypes({
  size: {
    // Size in device pixels that may be used for widths, heights, padding and spacing.
    default: 0,
    type: types.size,
    nullable: true,
    values: {
      none: null,
      full: '100%',
      twoThirds: '66.67%',
      zero: 0,
      ...viewportSizes
    }
  },
  border: {
    // Width of a border line. To avoid layout shifts where border tokens change, implementations
    // should consider if they need to also subtract this from an internal padding size.
    default: 0,
    nullable: true,
    type: types.number,
    values: {
      zero: 0,
      none: null
    }
  },
  color: {
    // Accepts as input any colour value understood by TinyColor2 library, which accepts
    // all common web formats and supports alpha transparency.
    default: null,
    nullable: true,
    type: types.color,
    values: {
      transparent: 'transparent',
      none: null
    }
  },
  display: {
    // The display CSS property sets whether an element is treated as a block or inline box and
    // the layout used for its children, such as flow layout, grid or flex.
    default: 'inline',
    nullable: true,
    type: types.display,
    values: {
      inline: 'inline',
      inlineBlock: 'inline-block',
      none: null
    }
  },
  radius: {
    default: 0,
    nullable: true,
    type: types.number,
    values: {
      none: null,
      zero: 0,
      // On both React Native and Web, a border radius greater than width/height
      // creates a circle or pill effect. Use this to create circles or near-circles;
      // for pill effects around text, where possible use a more reasonable value
      // to allow for text that is larger than expected due to, for example, user font scaling
      round: 99999999999999
    }
  },
  shadow: {
    // An object describing an intended shadow affect. Different platforms will need different
    // interpretations of these properties to give as close an approximation of the designer's
    // intent as is reasonably possible on each platform.
    default: null,
    nullable: true,
    type: 'null|{"blur": Number, "color": String, "inset": Boolean, "offsetX": Number, "offsetY": Number, "spread": Number}',
    values: {
      none: null
    }
  },
  fontSize: {
    // An intended font size, in device pixels, before applying user accessibility settings.
    default: fontBasePixels,
    nullable: true,
    type: types.fontSize,
    values: {
      none: null
    }
  },
  lineHeight: {
    // Line heights are applied as relative multipliers on font size
    default: 1,
    // check this matches RN / RNW behaviour,
    nullable: false,
    type: 'Number'
  },
  flexJustifyContent: {
    // Denotes that an element should have similar layout behaviour to an element in the
    // Yoga layout engine (https://yogalayout.com/) with this `justify-content` value.
    default: 'flex-start',
    type: types.enum,
    nullable: false,
    values: {
      center: 'center',
      flexStart: 'flex-start',
      spaceBetween: 'space-between'
    }
  },
  flexAlign: {
    // Denotes alignment behaviour similar to the Yoga layout engine (https://yogalayout.com/).
    // Depending on the token, this may apply to the element itself (like `align-self`), its
    // children in a flex container (like `align-items`), or between lines in a multi-line
    // wrapping element (like `align-content`). May need special handling on some platforms.
    default: 'flex-start',
    type: types.enum,
    nullable: false,
    values: {
      center: 'center',
      flexStart: 'flex-start',
      stretch: 'stretch'
    }
  },
  fontName: {
    // Font family name of a custom palette font. This should be only the typeface name and will be
    // concatenated with font weight and font style to reference a specific palette font file.
    default: null,
    nullable: true,
    type: 'null|String'
  },
  fontWeight: {
    // A numeric string representation of a font weight. When used with a custom font via `fontName` tokens,
    // this is appended to the fontName when choosing which palette font file to use, before `fontStyle`.
    default: '400',
    nullable: false,
    type: 'String'
  },
  fontStyle: {
    // A string denoting a font style (e.g. italic). When used with a custom font via `fontName` tokens,
    // this is appended to the fontName and fontWeight to complete a palette font file selection.
    default: 'normal',
    nullable: false,
    type: types.enum,
    values: {
      normal: 'normal',
      italic: 'italic'
    }
  },
  opacity: {
    // A floating point number between 0 and 1, where 1 is fully opaque and 0 is fully transparent.
    default: 1,
    nullable: false,
    type: 'Number',
    values: {
      opaque: 1
    }
  },
  overflow: {
    // A string describing how an element should handle overflow.
    default: 'visible',
    nullable: true,
    type: types.enum,
    values: {
      visible: 'visible',
      hidden: 'hidden',
      scroll: 'scroll',
      auto: 'auto',
      clip: 'clip',
      inherit: 'inherit',
      initial: 'initial',
      unset: 'unset'
    }
  },
  integer: {
    // A fallback token type for integers that have context-specific meaning. Use sparingly.
    default: null,
    nullable: true,
    type: 'Number',
    // Magic proxy enabling any integer to be specified in a theme, e.g. "{system.integer.42}" --> `42`
    values: integer
  },
  icon: {
    // In theme.json, state the name of an icon that exists in a theme's target palette.
    // React Native theme builds resolve this to a ready-to-use ReactNativeSVG component.
    // e.g. `"{palette.icon.CheckMark}"` will resolve as a CheckMark SVG function component
    // in the theme's React Native build, or the string "PaletteIconCheckMark" in ios/android.
    default: null,
    nullable: true,
    type: 'String|ReactNativeSVGComponent',
    values: {
      none: null
    }
  },
  textLine: {
    // String describing typographic text line effects e.g. underline, strikethrough
    default: 'none',
    nullable: false,
    type: types.enum,
    values: {
      none: 'none',
      underline: 'underline',
      strikethrough: 'strikethrough'
    }
  },
  textLineStyle: {
    // String describing the style of a particular textLine e.g. dotted, dashed, solid
    default: 'solid',
    nullable: false,
    type: types.enum,
    values: {
      solid: 'solid'
    }
  },
  position: {
    // String describing the placement of a sub-element relative to a container or main element
    default: null,
    nullable: true,
    type: types.enum,
    values: {
      bottom: 'bottom',
      left: 'left',
      right: 'right',
      top: 'top'
    }
  },
  direction: {
    // String describing how a stacking container should behave relative to its direct children
    default: 'column',
    nullable: false,
    type: types.enum,
    values: {
      column: 'column',
      // Stacks children vertically
      row: 'row',
      // Stacks children horizontally
      rowReverse: 'row-reverse' // Stacks children horizontally, but reversed
    }
  },
  show: {
    // Boolean toggle stating whether a sub-element should be shown or hidden.
    // Take care in component implementations around timing of appearing/disappearing animations
    // and focusability of children; implementation will rarely be just a simple on/off.
    default: true,
    nullable: false,
    type: 'Boolean',
    values: {
      true: true,
      false: false
    }
  },
  borderStyle: {
    // How a border line (if there is one) should be drawn e.g. solid, dotted, dashed etc.
    default: 'solid',
    nullable: false,
    type: types.enum,
    values: {
      none: 'none',
      solid: 'solid'
    }
  },
  letterSpacing: {
    // A relative adjustment, positive or negative, used to control typography spacing between characters.
    default: 0,
    nullable: false,
    type: 'Number',
    values: {
      none: 0
    }
  },
  verticalAlign: {
    // The intended alignment of an element within a flow of content, e.g. an image in inline text.
    // Implementations may need to map this to different properties depending on context.
    default: 'baseline',
    nullable: false,
    type: types.enum,
    values: {
      top: 'top',
      middle: 'middle',
      baseline: 'baseline'
    }
  },
  duration: {
    // A number of milliseconds that something like a transition animation should persist for.
    // Palettes may define common transition times used for common animation types.
    default: 0,
    nullable: false,
    type: 'Number',
    values: {
      zero: 0
    }
  },
  iconScale: {
    // A multiplier to apply to an element or sub-element's size, e.g. icon "bulge" effects.
    // Such scaling should be implemented in a way that does not affect layout.
    default: 1,
    nullable: false,
    type: 'Number',
    values: {
      scale1: 1,
      scale1_10: 1.1,
      scale1_25: 1.25
    }
  },
  textAlign: {
    // The horizontal alignment of flowing text within its immediate container.
    default: 'left',
    nullable: false,
    type: types.enum,
    values: {
      left: 'left',
      center: 'center',
      right: 'right'
    }
  },
  gradient: {
    // An object describing an intended gradient affect. Different platforms will need different
    // interpretations of these properties to give as close an approximation of the designer's
    // intent as is reasonably possible on each platform.
    default: null,
    nullable: true,
    type: '{"type": ["linear"|"radial"], "angle": Number, "stops": Array<{"stop": Number, "color": String, "hint": Number}>}',
    values: {
      none: null
    }
  },
  textTransform: {
    default: 'none',
    type: types.enum,
    nullable: false,
    values: {
      none: 'none',
      uppercase: 'uppercase'
    }
  }
});
export default tokenTypes;